import React, { useEffect, useState } from 'react'
import { MdNotifications, MdClose, MdCheckCircle } from 'react-icons/md'
import { useOneSignal } from '@/lib/hooks/use-onesignal'
import { InlineLoading } from '../inline-loading'
import { useClientLabel } from '@/lib/hooks/use-client-label'

const PROMPT_DISMISSED_KEY = 'push_prompt_dismissed'
const PROMPT_DISMISSED_DURATION = 5 * 24 * 60 * 60 * 1000 // 7 dias

export const PushNotificationPrompt: React.FC = () => {
    const { isInitialized, isSubscribed, isLoading, requestPermission } =
        useOneSignal()
    const [isVisible, setIsVisible] = useState(false)
    const [isRequesting, setIsRequesting] = useState(false)
    const { clientLabel } = useClientLabel()

    useEffect(() => {
        // Não exibir se ainda estiver carregando
        if (isLoading || !isInitialized) {
            return
        }

        // Não exibir se já estiver inscrito
        if (isSubscribed) {
            return
        }

        // Verificar se foi dispensado recentemente
        const dismissedAt = localStorage.getItem(PROMPT_DISMISSED_KEY)
        if (dismissedAt) {
            const dismissedTime = parseInt(dismissedAt, 10)
            const now = Date.now()

            if (now - dismissedTime < PROMPT_DISMISSED_DURATION) {
                return
            }
        }

        // Aguardar 3 segundos antes de exibir o prompt
        const timer = setTimeout(() => {
            setIsVisible(true)
        }, 3000)

        return () => clearTimeout(timer)
    }, [isLoading, isInitialized, isSubscribed])

    const handleAllow = async () => {
        console.log('[PushPrompt] Requesting permission...')
        console.log('[PushPrompt] isInitialized:', isInitialized)
        console.log('[PushPrompt] isLoading:', isLoading)

        setIsRequesting(true)
        try {
            const result = await requestPermission()
            console.log('[PushPrompt] Permission result:', result)

            if (result) {
                setIsVisible(false)
            }
        } catch (error) {
            console.error('[PushPrompt] Error requesting permission:', error)
        } finally {
            setIsRequesting(false)
        }
    }

    const handleDismiss = () => {
        localStorage.setItem(PROMPT_DISMISSED_KEY, Date.now().toString())
        setIsVisible(false)
    }

    if (!isVisible) {
        return null
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full !max-w-lg mx-4 animate-slide-up !text-black">
                {/* Header */}
                <div className="relative bg-gradient-to-r from-secondary to-secondary p-6 rounded-t-2xl">
                    <button
                        onClick={handleDismiss}
                        className="absolute top-4 right-4 btn btn-ghost btn-sm btn-circle text-white hover:bg-white/20"
                        disabled={isRequesting}
                    >
                        <MdClose className="text-xl" />
                    </button>

                    <div className="flex items-center gap-4">
                        <div className="bg-white/20 p-4 rounded-full">
                            <MdNotifications className="text-4xl text-white" />
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold text-white">
                                Ative as Notificações
                            </h3>
                            <p className="text-white/90 text-sm">
                                Comunicação em tempo real
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                    <div className="alert alert-warning">
                        <MdCheckCircle className="text-2xl" />
                        <div>
                            <h4 className="font-bold">
                                Extremamente Importante!
                            </h4>
                            <p className="text-sm">
                                As notificações push são essenciais para
                                comunicação eficaz com sua empresa
                            </p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-start gap-3">
                            <div className="badge badge-secondary badge-lg mt-1">
                                1
                            </div>
                            <div>
                                <p className="font-semibold">
                                    Novos Atendimentos
                                </p>
                                <p className="text-sm text-black/80">
                                    Seja notificado instantaneamente quando um
                                    {clientLabel} iniciar um atendimento
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="badge badge-secondary badge-lg mt-1">
                                2
                            </div>
                            <div>
                                <p className="font-semibold">Novos Pedidos</p>
                                <p className="text-sm text-black/80">
                                    Receba alertas imediatos sobre novos pedidos
                                    realizados
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="badge badge-secondary badge-lg mt-1">
                                3
                            </div>
                            <div>
                                <p className="font-semibold">
                                    Solicitações Urgentes
                                </p>
                                <p className="text-sm text-black/80">
                                    Não perca nenhuma solicitação de atendimento
                                    humano
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="divider"></div>
                </div>

                {/* Actions */}
                <div className="p-6 pt-0 flex gap-3">
                    <button
                        onClick={handleDismiss}
                        className="btn btn-ghost flex-1 !text-black"
                        disabled={isRequesting}
                    >
                        Agora não
                    </button>
                    <button
                        onClick={handleAllow}
                        className="btn btn-secondary flex-1 !text-black"
                        disabled={isRequesting}
                    >
                        {isRequesting ? (
                            <>
                                <InlineLoading visible={true} /> por favor
                                aguarde...
                            </>
                        ) : (
                            <>
                                <MdNotifications className="text-xl" />
                                Ativar Notificações
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}
